import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Pencil, Users, CheckCircle2, Zap, LayoutDashboard, Database, HardDrive, Key, Globe } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"

import { api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { cn } from "@/lib/utils"

interface PlanPrice {
  id: string
  currency: string
  amount_monthly: string
  amount_annual: string
}

interface Plan {
  id: string
  name: string
  display_name: string
  description: string
  plan_type: string
  property_limit: number | null
  tenant_limit: number | null
  lease_limit: number | null
  document_limit: number | null
  storage_limit_gb: number | null
  features: { multi_currency: boolean; listings: boolean; api_access: boolean }
  is_active: boolean
  free_trial_days: number
  subscriber_count: number
  user_count: number
  prices: PlanPrice[]
}

const editSchema = z.object({
  display_name: z.string().min(1),
  description: z.string().optional(),
  property_limit: z.preprocess((v) => (v === "" || v === null ? null : Number(v)), z.number().int().positive().nullable()),
  tenant_limit: z.preprocess((v) => (v === "" || v === null ? null : Number(v)), z.number().int().positive().nullable()),
  lease_limit: z.preprocess((v) => (v === "" || v === null ? null : Number(v)), z.number().int().positive().nullable()),
  document_limit: z.preprocess((v) => (v === "" || v === null ? null : Number(v)), z.number().int().positive().nullable()),
  storage_limit_gb: z.preprocess((v) => (v === "" || v === null ? null : Number(v)), z.number().positive().nullable()),
  free_trial_days: z.preprocess((v) => Number(v), z.number().int().min(0)),
  features: z.object({
    multi_currency: z.boolean(),
    listings: z.boolean(),
    api_access: z.boolean(),
  }),
})

type EditForm = z.infer<typeof editSchema>

function LimitRow({ icon: Icon, label, val }: { icon: any, label: string, val: number | null }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-0">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      {val === null ? (
        <span className="text-sm font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Unlimited</span>
      ) : (
        <span className="text-sm font-semibold">{val}</span>
      )}
    </div>
  )
}

function FeatureRow({ label, active }: { label: string, active: boolean }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      {active ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
      ) : (
        <div className="h-4 w-4 rounded-full border-2 border-muted" />
      )}
    </div>
  )
}

export default function AdminPlansPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<Plan | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)

  const { data: plans = [], isLoading } = useQuery<Plan[]>({
    queryKey: ["admin", "plans"],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/plans")
      return data.data ?? []
    },
  })

  // Sort plans: free -> pro -> premium
  const sortedPlans = [...plans].sort((a, b) => {
    if (a.plan_type === "free") return -1
    if (b.plan_type === "free") return 1
    return (a.prices[0]?.amount_monthly || "0") > (b.prices[0]?.amount_monthly || "0") ? 1 : -1
  })

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: EditForm }) => {
      const { data } = await api.put(`/api/admin/plans/${id}`, body)
      return data.data
    },
    onSuccess: () => {
      toast.success("Plan updated successfully")
      qc.invalidateQueries({ queryKey: ["admin", "plans"] })
      qc.invalidateQueries({ queryKey: ["plans"] })
      qc.invalidateQueries({ queryKey: ["plan", "usage"] })
      setSheetOpen(false)
    },
    onError: () => toast.error("Failed to update plan"),
  })

  const form = useForm<EditForm>({
    resolver: zodResolver(editSchema),
  })

  function openEdit(plan: Plan) {
    setEditing(plan)
    form.reset({
      display_name: plan.display_name ?? "",
      description: plan.description ?? "",
      property_limit: plan.property_limit,
      tenant_limit: plan.tenant_limit,
      lease_limit: plan.lease_limit,
      document_limit: plan.document_limit,
      storage_limit_gb: plan.storage_limit_gb,
      free_trial_days: plan.free_trial_days,
      features: {
        multi_currency: plan.features?.multi_currency ?? false,
        listings: plan.features?.listings ?? false,
        api_access: plan.features?.api_access ?? false,
      },
    })
    setSheetOpen(true)
  }

  function onSubmit(values: EditForm) {
    if (!editing) return
    update.mutate({ id: editing.id, body: values })
  }

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Subscription Plans</h1>
        <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
          Manage pricing tiers, resource limits, and feature access. Changes immediately affect new checkouts, while existing active subscriptions retain their current cycle terms.
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-[600px] rounded-3xl" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          {sortedPlans.map((plan) => {
            const isPremium = plan.name?.toLowerCase().includes("premium")
            const price = plan.prices?.[0]?.amount_monthly
              ? `${plan.prices[0].currency} ${parseFloat(plan.prices[0].amount_monthly).toFixed(0)}`
              : "Free"

            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative flex flex-col h-full rounded-3xl overflow-hidden transition-all duration-300",
                  isPremium ? "border-indigo-200 shadow-xl shadow-indigo-100" : "border-border shadow-sm"
                )}
              >
                {isPremium && (
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 to-white/10 pointer-events-none" />
                )}
                {isPremium && (
                  <div className="absolute top-0 right-0">
                    <div className="bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wider py-1 px-4 rounded-bl-xl shadow-sm">
                      Most Popular
                    </div>
                  </div>
                )}

                <CardHeader className="pb-4 relative z-10 px-6 pt-8">
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant={plan.plan_type === "free" ? "secondary" : "default"} className={cn(
                      "text-xs font-semibold px-2.5 py-0.5",
                      isPremium && "bg-indigo-600 hover:bg-indigo-700"
                    )}>
                      {plan.plan_type.toUpperCase()}
                    </Badge>
                  </div>
                  <CardTitle className="text-2xl font-bold">{plan.display_name}</CardTitle>
                  <CardDescription className="h-10 text-xs mt-2 line-clamp-2">
                    {plan.description}
                  </CardDescription>
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="text-4xl font-extrabold tracking-tight">{price}</span>
                    {plan.plan_type !== "free" && <span className="text-muted-foreground font-medium text-sm">/ mo</span>}
                  </div>
                </CardHeader>

                <CardContent className="flex-1 relative z-10 px-6 space-y-6 pb-6">
                  {/* Subscriber Count Indicator */}
                  <div className="flex items-center justify-between p-3 rounded-xl bg-muted/40 border border-border/50">
                    <div className="flex items-center gap-2">
                      <div className="p-1.5 rounded-md bg-background shadow-sm">
                        <Users className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm font-semibold">Active Customers</span>
                    </div>
                    <span className="font-bold text-lg">{plan.subscriber_count}</span>
                  </div>

                  {/* Limits */}
                  <div className="space-y-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Resource Limits</p>
                    <LimitRow icon={LayoutDashboard} label="Properties" val={plan.property_limit} />
                    <LimitRow icon={Users} label="Tenants" val={plan.tenant_limit} />
                    <LimitRow icon={Database} label="Leases" val={plan.lease_limit} />
                    <LimitRow icon={HardDrive} label="Storage (GB)" val={plan.storage_limit_gb} />
                  </div>

                  {/* Features */}
                  <div className="space-y-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Advanced Features</p>
                    <FeatureRow label="Multi-Currency Support" active={plan.features?.multi_currency} />
                    <FeatureRow label="Marketplace Listings" active={plan.features?.listings} />
                    <FeatureRow label="Developer API Access" active={plan.features?.api_access} />
                  </div>
                </CardContent>

                <CardFooter className="relative z-10 p-6 pt-0 mt-auto">
                  <Button
                    className={cn(
                      "w-full rounded-xl h-12 shadow-sm",
                      isPremium ? "bg-indigo-600 hover:bg-indigo-700 text-white" : ""
                    )}
                    variant={isPremium ? "default" : "outline"}
                    onClick={() => openEdit(plan)}
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit Configuration
                  </Button>
                </CardFooter>
              </Card>
            )
          })}
        </div>
      )}

      {/* Edit Configuration Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-md w-full border-l border-border shadow-2xl p-0 flex flex-col">
          <SheetHeader className="p-6 border-b border-border bg-muted/20">
            <SheetTitle className="text-xl font-bold flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-500" />
              Configure {editing?.display_name}
            </SheetTitle>
            <SheetDescription>
              Adjusting limits instantly applies to all new and existing users on this tier immediately.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="flex-1 overflow-y-auto flex flex-col">
            <div className="p-6 space-y-8 flex-1">

              {/* Branding */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Branding</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs">Display Name</Label>
                    <Input {...form.register("display_name")} className="mt-1.5 h-10 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-xs">Marketing Description</Label>
                    <Input {...form.register("description")} className="mt-1.5 h-10 rounded-xl" />
                  </div>
                </div>
              </div>

              {/* Hard Limits */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex justify-between">
                  <span>Resource Thresholds</span>
                  <span className="text-[10px] lowercase normal-case opacity-70">(leave blank for unlimited)</span>
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: "property_limit", label: "Properties", icon: LayoutDashboard, step: "1" },
                    { key: "tenant_limit", label: "Tenants", icon: Users, step: "1" },
                    { key: "lease_limit", label: "Leases", icon: Database, step: "1" },
                    { key: "document_limit", label: "Documents", icon: HardDrive, step: "1" },
                    { key: "storage_limit_gb", label: "Storage (GB)", icon: Globe, step: "0.01" },
                    { key: "free_trial_days", label: "Trial Duration (Days)", icon: Key, step: "1" },
                  ].map(({ key, label, icon: Icon, step }) => (
                    <div key={key} className="space-y-1.5 bg-muted/30 p-3 rounded-xl border border-border/50">
                      <Label className="text-xs flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                        {label}
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        step={step}
                        placeholder="∞"
                        className="h-9 rounded-lg bg-background"
                        {...form.register(key as keyof EditForm)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Feature Toggles */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Module Access</h3>
                <div className="space-y-3">
                  {[
                    { key: "features.multi_currency", label: "Global Multi-Currency Engine", desc: "Allow landlords to accept and convert foreign currencies." },
                    { key: "features.listings", label: "Public Property Marketplace", desc: "Enable hosting of public listings for vacant properties." },
                    { key: "features.api_access", label: "Developer API Integration", desc: "Provide programmatic REST API access tokens." },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="flex items-start justify-between p-4 rounded-xl border border-border bg-card shadow-sm">
                      <div className="pr-4">
                        <Label className="text-sm font-semibold cursor-pointer block mb-1">{label}</Label>
                        <p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
                      </div>
                      <Switch
                        className="mt-1 data-[state=checked]:bg-emerald-500"
                        checked={form.watch(key as "features.multi_currency" | "features.listings" | "features.api_access")}
                        onCheckedChange={(v) => form.setValue(key as "features.multi_currency" | "features.listings" | "features.api_access", v)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <SheetFooter className="p-6 border-t border-border bg-muted/10 mt-auto">
              <Button type="button" variant="ghost" onClick={() => setSheetOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={update.isPending} className="rounded-xl shadow-sm px-8">
                {update.isPending ? "Applying Configuration..." : "Save Configuration"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  )
}
